import { getChatCompletion } from '../aiIntegrations/chatCompletion';
import { supabase } from '../../lib/supabase';

const PERSONA_GUIDANCE = {
  'insurance-adjuster': {
    focus: 'Coverage decisions, subrogation potential, claim validity',
    decisionMatrix: ['Coverage determination', 'Subrogation viability', 'Reserve recommendations', 'Investigation scope']
  },
  'plaintiff-attorney': {
    focus: 'Liability theories, damages quantification, expert testimony needs',
    decisionMatrix: ['Case viability', 'Liability theories', 'Damages assessment', 'Expert witness requirements']
  },
  'defense-attorney': {
    focus: 'Exposure assessment, alternative causation theories, settlement posture',
    decisionMatrix: ['Exposure analysis', 'Defense strategies', 'Settlement range', 'Discovery priorities']
  },
  'siu-investigator': {
    focus: 'Fraud indicators, arson detection, inconsistency analysis',
    decisionMatrix: ['Fraud indicators', 'Arson probability', 'Red flags', 'Investigation recommendations']
  },
  'subrogation-specialist': {
    focus: 'Recovery potential, third-party liability, subrogation viability',
    decisionMatrix: ['Recovery likelihood', 'Third-party liability', 'Subrogation value', 'Legal strategy']
  },
  'prosecutor': {
    focus: 'Criminal elements, malice indicators, evidence sufficiency',
    decisionMatrix: ['Criminal viability', 'Malice evidence', 'Prosecution strength', 'Additional evidence needed']
  },
  'fire-marshal': {
    focus: 'Code compliance, fire safety violations, prevention recommendations',
    decisionMatrix: ['Code violations', 'Safety deficiencies', 'Prevention measures', 'Enforcement actions']
  },
  'risk-manager': {
    focus: 'Risk mitigation, prevention strategies, loss control',
    decisionMatrix: ['Risk factors', 'Mitigation strategies', 'Prevention protocols', 'Policy recommendations']
  },
  'property-owner': {
    focus: 'Claim process, documentation requirements, recovery options',
    decisionMatrix: ['Claim status', 'Documentation needs', 'Recovery timeline', 'Next steps']
  }
};

const REPORT_SYSTEM_PROMPT = `You are a certified fire forensics expert generating a comprehensive preliminary analysis report following NFPA 921 and ASTM E2713 standards.

CRITICAL ANTI-HALLUCINATION RULES:
- Base ALL findings on provided evidence and analysis
- Use phrases like "based on provided images", "according to case information", "observable evidence suggests"
- Never fabricate measurements, timestamps, or witness statements not provided
- Clearly distinguish between observable facts and analytical conclusions
- State limitations explicitly when information is incomplete

Report Structure (14 Sections):
1. Executive Summary
2. Key Findings
3. Investigation Methodology
4. Scene Documentation
5. Fire Origin Analysis
6. Fire Cause Analysis
7. Fire Progression
8. Damage Assessment
9. Evidence Analysis
10. Timeline Reconstruction
11. Expert Recommendations
12. Limitations & Disclaimers
13. Appendix
14. Persona-Specific Guidance

Output Format: JSON with sections array. For the "type" field on each section use ONLY these exact values:
- "text" for narrative/paragraph content (use for most sections)
- "list" for bullet-point lists (only when content is truly a list of items)
- "table" for tabular data with headers and rows
Never use any other type value.`;

export async function generateForensicReport(caseData, triageData, clarifyingAnswers, imageAnalysis, persona) {
  const personaConfig = PERSONA_GUIDANCE?.[persona] || PERSONA_GUIDANCE?.['insurance-adjuster'];

  const userPrompt = `
Generate comprehensive fire forensics report:

Case Information:
- Case Number: ${caseData?.case_number}
- Incident Date: ${caseData?.incident_date}
- Location: ${caseData?.property_address}
- Property Type: ${caseData?.property_type}
- Allegation: ${caseData?.allegation_type}
- Estimated Loss: $${caseData?.estimated_loss || 'Not provided'}

Triage Analysis:
${triageData?.summary}
Confidence: ${(triageData?.confidence * 100)?.toFixed(0)}%
Observable Findings:
${triageData?.observableFindings?.map(f => `- ${f}`)?.join('\n')}

Clarifying Questions & Answers:
${clarifyingAnswers?.map(qa => `Q: ${qa?.question_text}\nA: ${qa?.answer_text || 'Not answered'}`)?.join('\n\n')}

Image Analysis:
${imageAnalysis?.map((img, idx) => `Image ${idx + 1}: ${img?.description}`)?.join('\n')}

Persona: ${persona}
Focus Areas: ${personaConfig?.focus}
Decision Matrix Categories: ${personaConfig?.decisionMatrix?.join(', ')}

Generate a COMPREHENSIVE and DETAILED fire forensics report with ALL 14 sections. CRITICAL REQUIREMENTS:
- Each section MUST contain multiple paragraphs of substantive analysis (minimum 100+ words per section)
- Reference specific evidence, images, and Q&A answers throughout
- Section 1 (Executive Summary): Full overview of findings, origin/cause determination, and recommendations
- Section 14 (Persona-Specific Guidance): Include the decision matrix with categories: ${personaConfig?.decisionMatrix?.join(', ')}
- Do NOT produce placeholder or skeleton content — every section must have real analytical substance
- Use professional forensic report language throughout`;

  try {
    const { data: settingData, error: settingError } = await supabase?.from('system_settings')?.select('setting_value')?.eq('setting_key', 'ai_model_phase3')?.single();

    if (settingError) {
      console.error('Error fetching Phase 3 model setting:', settingError);
    }

    const modelToUse = settingData?.setting_value || 'gpt-4o';

    const response = await getChatCompletion(
      'OPEN_AI',
      modelToUse,
      [
        { role: 'system', content: REPORT_SYSTEM_PROMPT },
        { role: 'user', content: userPrompt }
      ],
      {
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'ForensicReport',
            schema: {
              type: 'object',
              properties: {
                sections: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      number: { type: 'string' },
                      title: { type: 'string' },
                      content: { type: 'string' },
                      type: { type: 'string' }
                    },
                    required: ['number', 'title', 'content', 'type'],
                    additionalProperties: false
                  }
                }
              },
              required: ['sections'],
              additionalProperties: false
            },
            strict: true
          }
        },
        max_completion_tokens: 16384
      }
    );

    // Check for truncated response
    const finishReason = response?.choices?.[0]?.finish_reason;
    if (finishReason === 'length') {
      console.warn('Report generation was TRUNCATED (finish_reason=length). Token limit may be too low.');
    }

    const content = response?.choices?.[0]?.message?.content;
    if (!content) {
      const apiError = response?.error || response?.details || JSON.stringify(response);
      console.error('Report generation returned no content. Full response:', JSON.stringify(response)?.substring(0, 2000));
      throw new Error(`AI model returned no content. Details: ${apiError}`);
    }

    // Strip markdown code fences if the model wrapped the JSON
    const rawJson = content.replace(/^```(?:json)?\n?|\n?```$/g, '').trim();
    const reportData = JSON.parse(rawJson);

    // Validate report has real content
    const sections = reportData?.sections || [];
    const totalContentLength = sections.reduce((sum, s) => sum + (s?.content?.length || 0), 0);
    console.log(`Report generated: ${sections.length} sections, ${totalContentLength} chars total content, finish_reason=${finishReason}`);

    if (sections.length === 0) {
      throw new Error('Report generation produced zero sections');
    }

    if (totalContentLength < 500) {
      console.error('Report content suspiciously short — sections:', sections.map(s => ({ title: s?.title, len: s?.content?.length })));
      throw new Error(`Report content is nearly empty (${totalContentLength} chars across ${sections.length} sections). This usually means the AI token limit was reached.`);
    }

    // Normalize section types — the AI sometimes returns 'paragraph', 'analysis',
    // 'structured', etc. Only 'list' and 'table' have special renderers; everything
    // else must be 'text' or the viewer renders a blank section.
    const normalizedSections = sections.map(s => ({
      ...s,
      type: (s?.type === 'list' || s?.type === 'table') ? s.type : 'text'
    }));

    return {
      success: true,
      data: { ...reportData, sections: normalizedSections },
      usage: response?.usage
    };
  } catch (error) {
    console.error('Report generation error:', error);
    return {
      success: false,
      error: error?.message || 'Failed to generate report'
    };
  }
}